import React, { useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme, Chip, CircularProgress, LinearProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendWhatsAppReport from './SendWhatsAppReport';
import { generateEnhancedPDF } from './PDFExport';

const ReportHeader = ({ 
  onBackToFilter, 
  onExport, 
  timeFilter, 
  selectedAreas, 
  selectedLocationTypes,
  selectedPriorities,
  selectedDetections, // Changed from selectedDetection to selectedDetections
  reportData,
  totalEvents
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ percentage: 0, message: '', stage: '' });

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format areas for display
  const formatAreas = () => {
    if (!selectedAreas || selectedAreas.length === 0) return "All Areas";
    if (selectedAreas.includes("All")) return "All Areas";
    if (Array.isArray(selectedAreas)) {
      return selectedAreas.join(", ");
    }
    return String(selectedAreas);
  };

  // Format location types for display
  const formatLocationTypes = () => {
    if (!selectedLocationTypes || selectedLocationTypes.length === 0) return "All Categories";
    if (selectedLocationTypes.includes("All")) return "All Categories";
    if (Array.isArray(selectedLocationTypes)) {
      return selectedLocationTypes.join(", ");
    }
    return String(selectedLocationTypes);
  };

  // Format priorities for display
  const formatPriorities = () => {
    if (!selectedPriorities || selectedPriorities.length === 0) return "All Priorities";
    if (selectedPriorities.includes("All")) return "All Priorities";
    if (Array.isArray(selectedPriorities)) {
      return selectedPriorities.join(", ");
    }
    return String(selectedPriorities);
  };

  // Handle PDF generation
  const handlePdfDownload = async () => {
    // Check if report data is available
    if (!reportData || Object.keys(reportData).length === 0) {
      alert('No report data available to generate PDF. Please ensure filters are applied and data is loaded.');
      return;
    }

    // Check if there are any events in the report data
    const hasEvents = Object.values(reportData).some(events => 
      Array.isArray(events) && events.length > 0
    );

    if (!hasEvents) {
      alert('No events found in the current report to generate PDF.');
      return;
    }

    console.log('🚀 Starting PDF generation with data:', {
      reportData: Object.keys(reportData),
      totalEvents,
      selectedAreas,
      timeFilter
    });

    setIsPdfGenerating(true);
    setPdfProgress({ percentage: 0, message: 'Initializing PDF generation...', stage: 'init' });

    try {
      // Call the enhanced PDF generation function with progress callback
      const result = await generateEnhancedPDF(
        reportData,
        timeFilter,
        selectedAreas,
        (progress) => {
          console.log('📊 PDF Progress:', progress);
          setPdfProgress(progress);
        }
      );

      console.log('✅ PDF generated successfully:', result);
      
      // Show success message
      setPdfProgress({ 
        percentage: 100, 
        message: `PDF downloaded successfully! (${result.totalEvents} events, ${result.totalImages} images)`,
        stage: 'complete'
      });

      // Clear progress after a delay
      setTimeout(() => {
        setPdfProgress({ percentage: 0, message: '', stage: '' });
      }, 3000);

    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      
      setPdfProgress({ 
        percentage: 0, 
        message: `PDF generation failed: ${error.message}`,
        stage: 'error'
      });

      // Show user-friendly error
      alert(`Failed to generate PDF: ${error.message}\n\nPlease try again or contact support if the issue persists.`);

      // Clear error after delay
      setTimeout(() => {
        setPdfProgress({ percentage: 0, message: '', stage: '' });
      }, 5000);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      {/* Top row with buttons and title */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        width: '100%',
        mb: 1
      }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBackToFilter}
          variant="outlined"
          size="small"
          sx={{ 
            color: 'white', 
            borderColor: 'white',
            fontSize: '0.9rem',
            fontWeight: 'medium',
            minWidth: isMobile ? '40px' : undefined,
            py: 0.5,
            px: isMobile ? 1 : undefined,
            height: '36px'
          }}
        >
          {isMobile ? '' : 'BACK TO FILTERS'}
        </Button>

        {/* Center title - only show on larger screens */}
        {!isMobile && (
          <Typography variant="h6" sx={{ 
            fontSize: isTablet ? '1.2rem' : '1.4rem',
            fontWeight: 'bold',
            flexGrow: 1,
            textAlign: 'center'
          }}>
            Report Details
          </Typography>
        )}
        
        {/* Action buttons container */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* PDF Download Button */}
          <Button
            startIcon={isPdfGenerating ? 
              <CircularProgress size={16} sx={{ color: 'white' }} /> : 
              <PictureAsPdfIcon />
            }
            onClick={handlePdfDownload}
            disabled={isPdfGenerating}
            variant="contained"
            sx={{ 
              backgroundColor: '#e74c3c',
              '&:hover': {
                backgroundColor: '#c0392b',
              },
              '&:disabled': {
                backgroundColor: '#7f8c8d',
                opacity: 0.7
              },
              whiteSpace: 'nowrap',
              fontSize: '0.9rem',
              fontWeight: 'medium',
              py: 0.5,
              px: isMobile ? 1 : 1.5,
              height: '36px'
            }}
          >
            {isPdfGenerating 
              ? (isMobile ? '' : `${pdfProgress.percentage}%`) 
              : (isMobile ? '' : 'DOWNLOAD PDF')
            }
          </Button>

          {/* WhatsApp Button */}
          <Button
            startIcon={<WhatsAppIcon />}
            onClick={() => setWhatsappModalOpen(true)}
            variant="contained"
            color="success"
            size="small"
            sx={{ 
              whiteSpace: 'nowrap',
              fontSize: '0.9rem',
              fontWeight: 'medium',
              py: 0.5,
              px: isMobile ? 1 : 1.5,
              height: '36px'
            }}
          >
            {isMobile ? '' : 'SEND TO WHATSAPP'}
          </Button>
        </Box>
      </Box>

      {/* PDF Generation Progress - Show when generating */}
      {isPdfGenerating && (
        <Box sx={{ 
          mb: 1, 
          p: 2, 
          backgroundColor: 'rgba(231, 76, 60, 0.1)', 
          borderRadius: 1,
          border: '1px solid #e74c3c'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} sx={{ color: '#e74c3c' }} />
            <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 'medium', flexGrow: 1 }}>
              {pdfProgress.message || 'Generating PDF...'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#e74c3c' }}>
              {pdfProgress.percentage}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={pdfProgress.percentage} 
            sx={{
              backgroundColor: 'rgba(231, 76, 60, 0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#e74c3c'
              }
            }}
          />
        </Box>
      )}

      {/* Success/Error Message */}
      {!isPdfGenerating && pdfProgress.stage === 'complete' && (
        <Box sx={{ 
          mb: 1, 
          p: 1, 
          backgroundColor: 'rgba(46, 204, 113, 0.1)', 
          borderRadius: 1,
          border: '1px solid #2ecc71'
        }}>
          <Typography variant="body2" sx={{ color: '#2ecc71', fontWeight: 'medium' }}>
            ✅ {pdfProgress.message}
          </Typography>
        </Box>
      )}

      {!isPdfGenerating && pdfProgress.stage === 'error' && (
        <Box sx={{ 
          mb: 1, 
          p: 1, 
          backgroundColor: 'rgba(231, 76, 60, 0.1)', 
          borderRadius: 1,
          border: '1px solid #e74c3c'
        }}>
          <Typography variant="body2" sx={{ color: '#e74c3c', fontWeight: 'medium' }}>
            ❌ {pdfProgress.message}
          </Typography>
        </Box>
      )}
      
      {/* Time range and filter info */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 1,
        mb: 1
      }}>
        {/* Show title on mobile only here */}
        {isMobile && (
          <Typography variant="subtitle1" sx={{ 
            fontSize: '1.1rem',
            fontWeight: 'bold',
            width: '100%',
            textAlign: 'center',
            mb: 1
          }}>
            Report Details
          </Typography>
        )}
        
        <Typography variant="body2" sx={{ 
          fontSize: '0.85rem',
          opacity: 1,
          mr: isMobile ? 0 : 1.5
        }}>
          <strong>Time:</strong> {formatDate(timeFilter.startTime)} to {formatDate(timeFilter.endTime)}
        </Typography>
        
        {/* Filter chips */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: isMobile ? 'center' : 'flex-start',
          width: isMobile ? '100%' : 'auto'
        }}>
          <Chip 
            icon={<LocationOnIcon sx={{ fontSize: '1rem' }} />}
            label={formatAreas()} 
            variant="outlined"
            size="small"
            sx={{ 
              color: '#1ABC9C', 
              borderColor: '#1ABC9C',
              '& .MuiChip-icon': { color: '#1ABC9C' },
              height: '28px',
              '& .MuiChip-label': {
                fontSize: '0.85rem',
                px: 1
              }
            }}
          />
          
          <Chip 
            icon={<CategoryIcon sx={{ fontSize: '1rem' }} />}
            label={formatLocationTypes()} 
            variant="outlined"
            size="small"
            sx={{ 
              color: '#1ABC9C', 
              borderColor: '#1ABC9C',
              '& .MuiChip-icon': { color: '#1ABC9C' },
              height: '28px',
              '& .MuiChip-label': {
                fontSize: '0.85rem',
                px: 1
              }
            }}
          />

          {/* Show priorities if not all selected */}
          {!selectedPriorities?.includes("All") && selectedPriorities?.length > 0 && (
            <Chip 
              label={`Priority: ${formatPriorities()}`} 
              variant="outlined"
              size="small"
              sx={{ 
                color: '#FFA726', 
                borderColor: '#FFA726',
                height: '28px',
                '& .MuiChip-label': {
                  fontSize: '0.85rem',
                  px: 1
                }
              }}
            />
          )}
        </Box>
      </Box>
      
      <SendWhatsAppReport 
        open={whatsappModalOpen}
        onClose={() => setWhatsappModalOpen(false)}
        selectedAreas={selectedAreas}
        timeFilter={timeFilter}
        selectedDetection={selectedDetections}
        selectedLocationTypes={selectedLocationTypes}
      />
    </Box>
  );
};

export default ReportHeader;